import React, { useMemo, useState } from "react";
import {
  Activity,
  Bone,
  FileText,
  Pill,
  ShieldAlert,
  Stethoscope,
  HeartPulse,
} from "lucide-react";
import { AccordionSection } from "./AccordionSection";
import {
  AllergySectionForm,
  ClinicalScoreSectionForm,
  DocumentSectionForm,
  ImplantSectionForm,
  MedicineSectionForm,
  RehabProtocolSectionForm,
  SurgerySectionForm,
} from "./SectionForms";
import { SelectOption } from "./FormControls";
import { ErrorSuccessModal } from "../../Modals/ErrorSuccessModal";

import { usePatientSurgeries } from "../../hooks/usePatientSurgeries";
import { usePatientDocuments } from "../../hooks/usePatientDocuments";
import { usePatientImplants } from "../../hooks/usePatientImplants";
import { usePatientMedicines } from "../../hooks/usePatientMedicines";
import { usePatientAllergies } from "../../hooks/usePatientAllergies";
import { usePatientClinicalScore } from "../../hooks/usePatientClinicalScore";
import { usePatientRehabProtocols } from "../../hooks/usePatientRehabProtocols";

import { useSurgeryTypes } from "../../hooks/useSurgeryTypes";
import { useDocumentTypes } from "../../hooks/useDocumentTypes";
import { useImplants } from "../../hooks/useImplants";
import { useMedicines } from "../../hooks/useMedicines";
import { useMedicineDoses } from "../../hooks/useMedicineDoses";
import { useAllergies } from "../../hooks/useAllergies";
import { useRehabFacilities } from "../../hooks/useRahabFacilties";

export type PatientDataEntrySectionId =
  | "surgeries"
  | "documents"
  | "implants"
  | "medicines"
  | "allergies"
  | "clinicalScores"
  | "rehabProtocols";

export interface PatientDataEntryFormProps {
  /** Patient this form creates records for */
  patientId: number | string;
  /** Optional: open a specific section on mount */
  defaultOpenSection?: PatientDataEntrySectionId | null;
  /** Optional heading override */
  title?: string;
  className?: string;
}

const emptyToNull = (value?: string | null) => {
  if (value === undefined || value === null || value === "") return null;
  return value;
};

export const PatientDataEntryForm: React.FC<PatientDataEntryFormProps> = ({
  patientId,
  defaultOpenSection = null,
  title = "Καταχώρηση Ιατρικών Δεδομένων",
  className,
}) => {
  const patientIdStr = String(patientId);
  const [openSection, setOpenSection] =
    useState<PatientDataEntrySectionId | null>(defaultOpenSection);

  // --- Patient-scoped create hooks ---
  const surgeries = usePatientSurgeries(patientIdStr);
  const documents = usePatientDocuments(patientIdStr);
  const implants = usePatientImplants(patientIdStr);
  const medicines = usePatientMedicines(patientIdStr);
  const allergies = usePatientAllergies(patientIdStr);
  const clinicalScores = usePatientClinicalScore(patientIdStr);
  const rehabProtocols = usePatientRehabProtocols(patientIdStr);

  // --- Master-data option sources ---
  // Note: `= []` in destructuring does NOT cover `null` (only undefined).
  const { surgeryTypes } = useSurgeryTypes();
  const { documentTypes } = useDocumentTypes();
  const { implants: implantCatalog } = useImplants();
  const { medicines: medicineCatalog } = useMedicines();
  const { medicineDoses } = useMedicineDoses();
  const { allergies: allergyCatalog } = useAllergies();
  const { rehabFacilities } = useRehabFacilities();

  const surgeryTypeOptions = useMemo<SelectOption[]>(
    () =>
      (surgeryTypes ?? []).map((st: any) => ({
        value: st.surgeryTypeId ?? st.SurgeryTypeId,
        label:
          st.surgeryTypeDescr ??
          st.SurgeryTypeDescr ??
          st.idDescr ??
          "Χωρίς περιγραφή",
      })),
    [surgeryTypes],
  );

  const documentTypeOptions = useMemo<SelectOption[]>(
    () =>
      (documentTypes ?? []).map((dt: any) => ({
        value: dt.documentTypeId ?? dt.DocumentTypeId,
        label:
          dt.documentDescr ??
          dt.DocumentDescr ??
          dt.idDescr ??
          "Χωρίς περιγραφή",
      })),
    [documentTypes],
  );

  const implantOptions = useMemo<SelectOption[]>(
    () =>
      (implantCatalog ?? []).map((item: any) => {
        const composed = [item.manufacturer, item.modelName]
          .filter(Boolean)
          .join(" · ");
        return {
          value: item.implantId ?? item.ImplantId,
          label:
            item.idDescr ??
            item.implantDescr ??
            (composed || "Χωρίς περιγραφή"),
        };
      }),
    [implantCatalog],
  );

  const medicineOptions = useMemo<SelectOption[]>(
    () =>
      (medicineCatalog ?? []).map((m: any) => ({
        value: m.medicineId ?? m.MedicineId,
        label:
          m.name ?? m.Name ?? m.medicineDescr ?? m.idDescr ?? "Χωρίς περιγραφή",
      })),
    [medicineCatalog],
  );

  const doseOptions = useMemo<SelectOption[]>(
    () =>
      (medicineDoses ?? []).map((d: any) => {
        const hours = d.hours ? `${d.hours}ώ` : null;
        const days = d.days ? `${d.days}η` : null;
        const parts = [hours, days].filter(Boolean).join(" / ");
        return {
          value: d.doseId ?? d.DoseId,
          label: parts || `Δόση #${d.doseId ?? d.DoseId}`,
        };
      }),
    [medicineDoses],
  );

  const allergyOptions = useMemo<SelectOption[]>(
    () =>
      (allergyCatalog ?? []).map((a: any) => ({
        value: a.allergyId ?? a.AllergyId,
        label:
          a.allergyDescr ?? a.AllergyDescr ?? a.idDescr ?? "Χωρίς περιγραφή",
      })),
    [allergyCatalog],
  );

  const rehabOptions = useMemo<SelectOption[]>(
    () =>
      (rehabFacilities ?? []).map((f: any) => ({
        value:
          f.rehabFacId ?? f.RehabFacId ?? f.rehabId ?? f.facilityId ?? f.id,
        label:
          f.rehabName ??
          f.RehabName ??
          f.facilityDescr ??
          f.protocolType ??
          "Χωρίς περιγραφή",
      })),
    [rehabFacilities],
  );

  /** Surgeries already saved for this patient — used when linking documents */
  const patientSurgeryOptions = useMemo<SelectOption[]>(
    () =>
      (surgeries.patientSurgeries ?? []).map((s: any) => {
        const typeLabel =
          surgeryTypeOptions.find(
            (o) => Number(o.value) === Number(s.surgeryTypeId),
          )?.label ?? "Χειρουργείο";
        const date = s.surgeryDate
          ? new Date(s.surgeryDate).toLocaleDateString("el-GR")
          : "-";
        return {
          value: s.surgeryId,
          label: `${typeLabel} (${date})`,
        };
      }),
    [surgeries.patientSurgeries, surgeryTypeOptions],
  );

  const feedbackModals = [
    surgeries.modalState,
    documents.modalState,
    implants.modalState,
    medicines.modalState,
    allergies.modalState,
    clinicalScores.modalState,
    rehabProtocols.modalState,
  ];
  const activeModal = feedbackModals.find((m) => m?.isOpen);
  const closeActiveModal = () => {
    surgeries.closeModal();
    documents.closeModal();
    implants.closeModal();
    medicines.closeModal();
    allergies.closeModal();
    clinicalScores.closeModal();
    rehabProtocols.closeModal();
  };

  const toggle = (id: PatientDataEntrySectionId) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div className={className}>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">
          Προσθέστε εγγραφές ανά κατηγορία για τον ασθενή{" "}
          <span className="font-medium text-slate-700">#{patientIdStr}</span>.
          Κάθε ενότητα αποθηκεύεται ανεξάρτητα.
        </p>
      </div>

      <div className="space-y-3">
        <AccordionSection
          id="surgeries"
          title="Χειρουργεία"
          description="Καταχώρηση επέμβασης με τύπο από το master Surgery Types"
          icon={<Bone className="h-4 w-4" />}
          isOpen={openSection === "surgeries"}
          onToggle={() => toggle("surgeries")}
        >
          <SurgerySectionForm
            options={surgeryTypeOptions}
            isLoading={surgeries.isLoading}
            onSave={async (values) => {
              await surgeries.createPatientSurgery({
                ...values,
                category: emptyToNull(values.category),
                startTime: emptyToNull(values.startTime),
                endTime: emptyToNull(values.endTime),
                preOpDiagnosis: emptyToNull(values.preOpDiagnosis),
                postOpDiagnosis: emptyToNull(values.postOpDiagnosis),
                remarks: emptyToNull(values.remarks),
                active: true,
              });
            }}
          />
        </AccordionSection>

        <AccordionSection
          id="documents"
          title="Αποτελέσματα Εξετάσεων"
          description="Ανέβασμα αρχείου με σύνδεση σε χειρουργεία"
          icon={<FileText className="h-4 w-4" />}
          isOpen={openSection === "documents"}
          onToggle={() => toggle("documents")}
        >
          <DocumentSectionForm
            documentTypeOptions={documentTypeOptions}
            surgeryOptions={patientSurgeryOptions}
            isLoading={documents.isLoading}
            onSave={async (values) => {
              await documents.createPatientDocument({
                documentTypeId: values.documentTypeId,
                fileName: values.fileName,
                format: values.format,
                file: values.file,
                examinationDate:
                  emptyToNull(values.examinationDate) ?? undefined,
                medicalCenter: emptyToNull(values.medicalCenter) ?? undefined,
                documentRemarks:
                  emptyToNull(values.documentRemarks) ?? undefined,
                patientSurgeryIds: values.patientSurgeryIds ?? [],
              });
            }}
          />
        </AccordionSection>

        <AccordionSection
          id="implants"
          title="Εμφυτεύματα"
          description="Επιλογή μοντέλου από τον κατάλογο εμφυτευμάτων"
          icon={<Activity className="h-4 w-4" />}
          isOpen={openSection === "implants"}
          onToggle={() => toggle("implants")}
        >
          <ImplantSectionForm
            options={implantOptions}
            isLoading={implants.isLoading}
            onSave={async (values) => {
              await implants.createPatientImplant({
                ...values,
                active: true,
              } as any);
            }}
          />
        </AccordionSection>

        <AccordionSection
          id="medicines"
          title="Φαρμακευτικές Αγωγές"
          description="Συνταγογράφηση με φάρμακο και δόση από master tables"
          icon={<Pill className="h-4 w-4" />}
          isOpen={openSection === "medicines"}
          onToggle={() => toggle("medicines")}
        >
          <MedicineSectionForm
            medicineOptions={medicineOptions}
            doseOptions={doseOptions}
            isLoading={medicines.isLoading}
            onSave={async (values) => {
              await medicines.createPatientMedicine({
                ...values,
                duration: emptyToNull(values.duration),
                patMedRemarks: emptyToNull(values.patMedRemarks),
                active: true,
              } as any);
            }}
          />
        </AccordionSection>

        <AccordionSection
          id="allergies"
          title="Αλλεργίες"
          description="Σύνδεση ασθενούς με αλλεργία από τον κατάλογο"
          icon={<ShieldAlert className="h-4 w-4" />}
          isOpen={openSection === "allergies"}
          onToggle={() => toggle("allergies")}
        >
          <AllergySectionForm
            options={allergyOptions}
            isLoading={allergies.isLoading}
            onSave={async (values) => {
              await allergies.createPatientAllergy({
                allergyId: values.allergyId,
                patAllRemarks: emptyToNull(values.patAllRemarks),
                active: true,
              });
            }}
          />
        </AccordionSection>

        <AccordionSection
          id="clinicalScores"
          title="Κλινικά Scores"
          description="VAS πόνου και βαθμολογία κινητικότητας"
          icon={<Stethoscope className="h-4 w-4" />}
          isOpen={openSection === "clinicalScores"}
          onToggle={() => toggle("clinicalScores")}
        >
          <ClinicalScoreSectionForm
            isLoading={clinicalScores.isLoading}
            onSave={async (values) => {
              await clinicalScores.createPatientClinicalScore({
                ...values,
                scoreRemarks: emptyToNull(values.scoreRemarks),
                active: true,
              } as any);
            }}
          />
        </AccordionSection>

        <AccordionSection
          id="rehabProtocols"
          title="Πρωτόκολλα Αποκατάστασης"
          description="Ανάθεση πρωτοκόλλου / κέντρου αποκατάστασης"
          icon={<HeartPulse className="h-4 w-4" />}
          isOpen={openSection === "rehabProtocols"}
          onToggle={() => toggle("rehabProtocols")}
        >
          <RehabProtocolSectionForm
            options={rehabOptions}
            isLoading={rehabProtocols.isLoading}
            onSave={async (values) => {
              await rehabProtocols.createPatientRehabProtocol({
                ...values,
              } as any);
            }}
          />
        </AccordionSection>
      </div>

      {activeModal ? (
        <ErrorSuccessModal
          isOpen={activeModal.isOpen}
          type={activeModal.type}
          title={activeModal.title}
          message={activeModal.message}
          onClose={closeActiveModal}
        />
      ) : null}
    </div>
  );
};

export default PatientDataEntryForm;
